/*
 * Created Date: Wed, 9th Dec 2020, 16:49:46 pm
 * Author: Kristyna Fojtikova
 * Email: kristyna.fojtikova@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useCallback } from 'react';
import {
  Datagrid,
  List,
  TextField,
  NumberField,
  useTranslate,
  usePermissions,
  Title,
  useGetList,
  useRedirect,
} from 'react-admin';
import { isProjectManager } from 'utils/roles';
import * as R from 'ramda';
import { EmptyResource } from 'Layout';
import { PostPagination } from '../../Components/CustomPagination/pagination';
import ListFilters from './filters';
import ListActions from './actions';
import { usePersistentOrganisation } from '../../Components/Contexts';

const ProjectList = props => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const redirect = useRedirect();
  const {
    persistentOrganisation,
    onSelectPersistentOrganisation,
  } = usePersistentOrganisation();

  const { total, loading, error } = useGetList('Organisation', {
    page: 1,
    perPage: 1,
  });
  const hasOrganisations = !loading && !error && total > 0;

  const EmptyProjectCallback = useCallback(() => {
    const EmptyProjects = () => {
      const title = hasOrganisations
        ? 'resources.Project.emptyStateTitle'
        : 'resources.Project.emptyStateNoOrgTitle';
      const subtitle = hasOrganisations
        ? 'resources.Project.emptyStateSubtitle'
        : 'resources.Project.emptyStateNoOrgSubtitle';
      const buttonLabel = hasOrganisations
        ? 'createProject'
        : 'createOrganisation';
      const basePath = hasOrganisations ? props.basePath : '/Organisation';
      return (
        <EmptyResource
          title={title}
          subtitle={subtitle}
          buttonLabel={buttonLabel}
          basePath={basePath}
        />
      );
    };
    return EmptyProjects();
  }, [hasOrganisations, redirect]);

  return (
    <>
      <Title title="resources.General.cmsTitle" />
      <List
        filterDefaultValues={
          persistentOrganisation.id
            ? {
                organisationId: persistentOrganisation.id,
              }
            : {}
        }
        empty={<EmptyProjectCallback />}
        title={translate('resources.Project.name', { smart_count: 2 })}
        bulkActionButtons={false}
        filters={
          <ListFilters organisationFilter={onSelectPersistentOrganisation} />
        }
        perPage={25}
        pagination={<PostPagination />}
        exporter={false}
        actions={<ListActions />}
        {...props}
      >
        <Datagrid
          rowClick={(id, basePath, record) => {
            const orgId = R.path(['organisation', 'id'], record);
            const isManager = isProjectManager(permissions, orgId);
            return isManager
              ? {
                  pathname: `/Project/${record.id}`,
                  state: {
                    organisationId: orgId,
                  },
                }
              : null;
          }}
        >
          <TextField
            source="name"
            label="resources.Project.fields.project"
            sortable={false}
          />
          <TextField
            source="organisation.name"
            label="resources.Project.fields.organisation"
            sortable={false}
          />
          <NumberField
            source="statistics.numberOfSites"
            label="resources.Project.fields.numberOfSites"
            sortable={false}
          />
          <NumberField
            source="statistics.numberOfUsers"
            label="resources.Project.fields.numberOfUsers"
            sortable={false}
          />
        </Datagrid>
      </List>
    </>
  );
};
export default ProjectList;
